if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        const thumbnailTargets = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
        thumbnailTargets.forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });

        // The mobile dot pagination lives on the main viewer (not inside the
        // thumbnails element the plain SliderComponent already knows how to
        // navigate), so it has no click behavior of its own — route each dot
        // to the same setActiveMedia() call as the matching thumbnail, and
        // keep dot -> mediaId lookups (both ways) for active-state syncing.
        this.dots = Array.from(this.elements.viewer.querySelectorAll('.slider-counter__link--dots'));
        this.dotsByMediaId = {};
        this.dots.forEach((dot, index) => {
          const target = thumbnailTargets[index];
          if (!target) return;
          this.dotsByMediaId[target.dataset.target] = dot;
          dot.addEventListener('click', this.setActiveMedia.bind(this, target.dataset.target, false));
        });

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        if (this.dataset.desktopLayout.includes('thumbnail')) this.initThumbnailScrollButtons();
      }

      // Dawn's SliderComponent only understands horizontal scrolling: its
      // prev/next disabled-state math is based on offsetLeft, which is ~0 for
      // every item once the thumbnail list is a vertical flex column (desktop
      // layout here), so it sees every thumbnail as "visible" and permanently
      // disables both buttons. Drive them ourselves instead, and re-correct
      // the disabled state after every scroll event (including the base
      // class's own no-op 'scroll' listener, which still runs first).
      initThumbnailScrollButtons() {
        const list = this.elements.thumbnails.querySelector('.thumbnail-list');
        const prevButton = this.elements.thumbnails.querySelector('.slider-button--prev');
        const nextButton = this.elements.thumbnails.querySelector('.slider-button--next');
        if (!list || !prevButton || !nextButton) return;

        const updateDisabled = () => {
          if (!this.mql.matches) return;
          const atTop = list.scrollTop <= 1;
          const atBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 1;
          prevButton.toggleAttribute('disabled', atTop);
          nextButton.toggleAttribute('disabled', atBottom);
        };

        const scrollByDirection = (direction) => {
          const item = list.querySelector('.thumbnail-list__item');
          const gap = parseFloat(getComputedStyle(list).rowGap) || 0;
          const step = item ? item.getBoundingClientRect().height + gap : list.clientHeight * 0.8;
          list.scrollBy({ top: direction * step, behavior: 'smooth' });
        };

        prevButton.addEventListener('click', (event) => {
          event.preventDefault();
          scrollByDirection(-1);
        });

        nextButton.addEventListener('click', (event) => {
          event.preventDefault();
          scrollByDirection(1);
        });

        list.addEventListener('scroll', updateDisabled);
        window.addEventListener('resize', updateDisabled);
        updateDisabled();
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement.dataset.mediaId;
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
        this.setActiveDot(mediaId);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        this.setActiveDot(mediaId);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveDot(mediaId) {
        if (!this.dots) return;
        this.dots.forEach((dot) => dot.classList.remove('slider-counter__link--active'));
        const activeDot = this.dotsByMediaId[mediaId];
        if (activeDot) activeDot.classList.add('slider-counter__link--active');
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
